import React from 'react';
import { printHtml } from '../../utils/printDocument';
import { savePdf } from '../../utils/pdfDocument';
import { formatClinicDate, formatClinicTime } from '../../utils/clinicTime';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { formatPaisa, formatRs, toPaisa } from '../../utils/money';

/**
 * The slip printed when a cash drawer closes: the cash that went in and out, what the drawer should
 * have held, what was counted, and what was taken by other methods. Made the way the token slip and
 * the invoice receipt are: the same markup is the preview on screen and, copied into a small window
 * under the thermal stylesheet below, the printed slip. The Tailwind classes style the preview; the
 * slip- classes style the paper.
 */

export const DRAWER_KIND_LABEL = {
  TOKEN_FEE: 'Token fees',
  TOKEN_FEE_CHANGE: 'Token fee changes',
  TOKEN_REFUND: 'Token refunds',
  REFERRAL_REFUND: 'Referral refunds',
  BILL_PAYMENT: 'Bill payments',
};

/** "Short Rs 200", "Over Rs 50" or "Balanced", from a signed rupee amount. */
export const overShortLabel = (overShort) => {
  const paisa = toPaisa(overShort);
  if (paisa === 0) return 'Balanced';
  return `${paisa > 0 ? 'Over' : 'Short'} ${formatRs(Math.abs(paisa) / 100)}`;
};

/** "15 Sept 2026 09:02 AM", in clinic time. */
export const drawerTime = (value) => (value
  ? `${formatClinicDate(value, { day: '2-digit', month: 'short', year: 'numeric' }, '')} ${formatClinicTime(value, { hour: '2-digit', minute: '2-digit', hour12: true }, '')}`.trim()
  : '');

/** An amount with its sign, "-500" for cash that went out. */
export const signedAmount = (rupees) => {
  const paisa = toPaisa(rupees);
  return paisa < 0 ? `-${formatPaisa(-paisa)}` : formatPaisa(paisa);
};

// The thermal-printer stylesheet, on the token slip's measurements: 80 mm, Courier, dashed rules.
const SLIP_STYLES = `
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 80mm; padding: 5mm; background: white; color: #000; }
  .slip-header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
  .slip-name { font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
  .slip-title { font-size: 13px; font-weight: bold; margin-top: 2px; }
  .slip-section { border-bottom: 1px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
  .slip-heading { font-weight: bold; margin-bottom: 2px; }
  .slip-row { display: flex; justify-content: space-between; gap: 8px; margin: 3px 0; }
  .slip-label { font-weight: bold; }
  .slip-value { text-align: right; }
  .slip-strong { font-size: 13px; font-weight: bold; }
  .slip-note { margin-top: 4px; }
  .slip-footer { text-align: center; margin-top: 10px; font-size: 11px; }
`;

const Row = ({ label, children, className = '' }) => (
  <div className={`slip-row flex justify-between gap-3 mb-1 ${className}`}>
    <span className="slip-label font-semibold">{label}</span>
    <span className="slip-value text-right tabular-nums">{children}</span>
  </div>
);

const Section = ({ children }) => (
  <div className="slip-section border-b border-dashed border-gray-400 pb-3 mb-3 text-sm">{children}</div>
);

/** The closing slip, from a count or close result: { drawer, summary }. */
export const DrawerSlip = ({ result, branding }) => {
  const drawer = result?.drawer;
  if (!drawer) return null;
  const summary = result.summary || {};
  const name = branding?.profile?.nameEnglish || 'Perveen Aslam Memorial Hospital';
  const kinds = summary.cashByKind || [];
  const others = summary.otherMethods || [];
  const difference = toPaisa(drawer.overShort);
  const counted = drawer.countedCash !== null && drawer.countedCash !== undefined;

  return (
    <div className="slip text-gray-900">
      <div className="slip-header text-center border-b border-dashed border-gray-400 pb-3 mb-3">
        <div className="slip-name text-lg font-bold tracking-wide uppercase leading-snug">{name}</div>
        <div className="slip-title text-sm font-semibold mt-1">Cash drawer closing</div>
      </div>

      <Section>
        <Row label="Cashier:">{drawer.cashierName || ''}</Row>
        <Row label="Opened:">{drawerTime(drawer.openedAt)}</Row>
        {drawer.closedAt && <Row label="Closed:">{drawerTime(drawer.closedAt)}</Row>}
      </Section>

      <Section>
        <Row label="Starting cash:">{signedAmount(summary.openingFloat ?? drawer.openingFloat)}</Row>
        {kinds.map((k) => (
          <Row key={k.key} label={`${DRAWER_KIND_LABEL[k.key] || k.key} (${k.count}):`}>{signedAmount(k.amount)}</Row>
        ))}
        <Row label="Expected cash:" className="slip-strong font-bold">
          {`Rs ${signedAmount(drawer.expectedCash ?? summary.expectedCash)}`}
        </Row>
        {counted && <Row label="Counted:">{`Rs ${signedAmount(drawer.countedCash)}`}</Row>}
        {counted && (
          <Row
            label={difference === 0 ? 'Difference:' : difference > 0 ? 'Over:' : 'Short:'}
            className="slip-strong font-bold"
          >
            {signedAmount(drawer.overShort)}
          </Row>
        )}
        {drawer.differenceReason && (
          <div className="slip-note mt-1 text-xs">{`Reason: ${drawer.differenceReason}`}</div>
        )}
      </Section>

      {others.length > 0 && (
        <Section>
          <div className="slip-heading font-semibold mb-1">Not in the drawer</div>
          {others.map((m) => (
            <Row key={m.key} label={`${PAYMENT_MODE_LABEL[m.key] || m.key} (${m.count}):`}>{signedAmount(m.amount)}</Row>
          ))}
        </Section>
      )}

      {drawer.closedByName && (
        <div className="slip-footer text-center mt-4 text-xs text-gray-600">{`Closed by: ${drawer.closedByName}`}</div>
      )}
    </div>
  );
};

/**
 * Prints the slip rendered in `node` in a small window of its own, the way a token slip prints.
 * Returns false when there is nothing to print or the browser blocked the window.
 */
export const printDrawerSlip = (node) => {
  if (!node) return false;
  return printHtml({
    title: 'Cash drawer closing',
    styles: SLIP_STYLES,
    bodyHtml: node.innerHTML,
    features: 'width=300,height=600',
  });
};

/** The slip rendered in `node`, saved as an 80 mm PDF named for the cashier and the day. */
export const saveDrawerSlipPdf = (node, drawer) => {
  const day = formatClinicDate(drawer?.openedAt, { day: '2-digit', month: 'short', year: 'numeric' }, '');
  const fileName = ['Cash drawer', drawer?.cashierName, day].filter(Boolean).join(' ');
  return savePdf({ title: 'Cash drawer closing', fileName, styles: SLIP_STYLES, bodyHtml: node.innerHTML, paper: 'SLIP' });
};
